<template>
  <Experiment title="Missing information trials">
    <InstructionScreen :title="'Welcome'">
      Thank you for taking part in our experiment! 
      <br />
      In this experiment, you will be asked to solve different tasks.
      The tasks include simple common sense questions or very simple math tasks.
      <b>Note that for some of the tasks, no definitive answer can be provided.</b>
      For example, the tasks may be unanswerable because the context is missing information.
      <br />
      You will first decide if a definitive answer can be provided or not through a button press, and then provide a solution and an optional explanation of your answer through typing.
      <br />
      <br />
      You will see detailed instructions and example trials on the next screens, before moving to the main experiment.
      The experiment contains seven trials in total.
      Your participation in this research is voluntary.
      You may decline to answer any or all of the following questions.
      You may decline further participation, at any time, without adverse consequences.
      <br />
      <br />
      Your anonymity is assured; the researchers who have requested your
      participation will not receive any personal information about you.
      <br />
      <br />
      By pressing the button 'Next' you confirm that you are at least 18 years old and agree to participate in this study. 
    </InstructionScreen>

    <InstructionScreen :title="'Step-by-step task instructions'">
      You will solve simple common sense questions or very simple math tasks.
      <br />
      Note that <b>for some of the tasks, no definitive answer can be provided.</b>
      For example, the tasks may be unanswerable because the context is missing information or there is insufficient evidence, or because the question is nonsensical, underspecified or makes incorrect assumptions.
      <b>In all of these cases, please indicate that the task is unanswerable.</b>
      You will see a hint of what counts as (un)answerable at the top of each trial.
      The tasks may also contain information that is irrelevant for solving the task.
      <br />
      <br />
      Throughout the experiment, you can earn a <b>total of 32 points</b>.
      <b>Each of the trials is worth a different amount of points.</b>
      For some trials, the amount will be indicated in the task instruction in the red box in the middle of the screen.
      You will receive the points for each trial <b>if you solve that trial correctly</b>, i.e., press the correct button in Step 1 (see below).
      <br />
      You will receive a <b>bonus payment of up to 0.20 if you get more than 28 points, proportionally to your total points</b>.
      <br /> 
      The task will be presented in a blue box below the instructions.
      <br />
      For each trial, please follow these steps:
      <br />
      <ol>
        <li><b>Decide:</b> Click the button to indicate if the task is 'Answerable' or 'Not definitively answerable'.</li>
        <li><b>Answer via typing:</b>
          <ul>
            <li><b>If 'Answerable':</b> Type your final answer in the main text field.</li>
            <li><b>If 'Not definitively answerable':</b> Use this text field to explain exactly why the task is not definitively answerable.</li>
          </ul>
        </li>
        <li><b>Optionally explain:</b> If you want or need to, use the second text field for your solution steps, or to add extra comments.</li>
      </ol>
      You may use a calculator or a pen and paper to solve the math tasks, if you wish.
      <br />
      Note that some of the trials will be <b>attention checking trials</b>. 
      You will recognize them immediately when you read the instructions in the red box in the middle of the screen carefully -- this trial contains instructions to press a specific button and type specific text into the text field. 
      Please precisely follow these instructions on the respective trial. 
      <br />
      Please click 'Next' to see the example trials.
    </InstructionScreen>
    <InstructionScreen :title="'Example screen'">
      <b>Here is an example of a not definitively answerable problem.</b>
      <br/>
      <br/>
      <div style="background-color: #f0f0f0; padding: 15px; border-radius: 5px;">
          Hint: <br />
          <b>Answerable:</b> given your knowledge and the provided context, you can answer the question with a concrete / specific, not imagined answer. <br />
          <b>Not definitvely answerable:</b> no specific answer can be provided because the context is missing information, the question is nonsensical, underspecified or makes incorrect assumptions.<br />
      </div>
      <br />
      <div style="background-color: #f7e0e0; padding: 15px; border-radius: 5px;">Please answer the following question.</div>
      <br />
      <div style="background-color: #e3f2fd; padding: 15px; border-radius: 5px;">Calculate the exact number of grains of sand on all the beaches in the world.</div>
      <br />
      <div style="text-align: center;">
        <img :src="require(`./img/not_answerable.png`)" :style="{ width: '350px' }"/>
      </div>

      
      Please write down your final answer to the task or why you consider it not answerable.
      <br />
      <textarea
        style="width: 500px; height: 50px"
      >
        The question cannot be answered because it asks for something that is impossible to determine.
      </textarea>
      <br />
      Please provide optional explanations of your solution steps.
      <textarea
        style="width: 500px; height: 200px"
      >
        There are too many grains of sand on a beach, they cannot be counted. 
      </textarea>
      <br />
    </InstructionScreen>
    <InstructionScreen :title="'Example screen'">
      <b>Here is an example of an answerable problem.</b>
      <br/>
      <br/>
      <div style="background-color: #f0f0f0; padding: 15px; border-radius: 5px;">
          Hint: <br />
          <b>Answerable:</b> given your knowledge and the provided context, you can answer the question with a concrete / specific, not imagined answer. <br />
          <b>Not definitvely answerable:</b> no specific answer can be provided because the context is missing information, the question is nonsensical, underspecified or makes incorrect assumptions.<br />
      </div>
      <br />
      <div style="background-color: #f7e0e0; padding: 15px; border-radius: 5px;">Please answer the following question.</div>
      <br />
      <div style="background-color: #e3f2fd; padding: 15px; border-radius: 5px;">Damien created a currency based on bottle caps and got his friends to take part. He finds 10 bottle caps a day on his way home, three of which are white, and each bottle cap is worth $0.25. How much money does he make in a 30-day month?</div>
      <br />
      <div style="text-align: center;">
        <img :src="require(`./img/answerable.png`)" :style="{ width: '350px' }"/>
      </div>
      Please write down your final answer to the task or why you consider it not answerable.
      <br />
      <textarea
        style="width: 500px; height: 50px"
      >
        $75
      </textarea>
        <br />
      Please provide optional explanations of your solution steps.
      <textarea
        style="width: 500px; height: 200px"
      >
        10 x 30 = 300 bottle caps in total.
        300 x $0.25 = $75.
      </textarea>
      <br />
      Please click 'Next' to start the experiment.
    </InstructionScreen>

    <template v-for="(trial, i) in trials">
      <TwoStepScreen :key=i :trial=trial :index=i :progress="i / trials.length" />
      <TotalPointsScreen v-if="i==trials.length-1" />
    </template>
    
    <PostTestScreen />
    <SubmitResultsScreen />
  </Experiment>
</template>

<script>
import _ from 'lodash';
import trials from '../trials/main_trials_points.csv';
import fillers from '../trials/fillers_points.csv';
import TwoStepScreen from './TwoStepScreen';
import TotalPointsScreen from './TotalPointsScreen';

// sample conditions with guaranteed validity
var group = _.sample(['odd', 'even']);

// Generate all 6 possible combinations of risk conditions and solvability
const risk_levels = ['high_risk', 'low_risk', 'default'];
const all_combinations = [];
for (const risk of risk_levels) {
  all_combinations.push(`${risk}-solvable`);
  all_combinations.push(`${risk}-unsolvable`);
}

// Pick a random solvable condition for the first commonsense trial (guarantees at least one solvable in commonsense)
const unsolvable_options = all_combinations.filter(c => c.includes('unsolvable'));
const commonsense_unsolvable = _.sample(unsolvable_options);

// Get remaining 5 combinations and shuffle them
const remaining_combinations = _.without(all_combinations, commonsense_unsolvable);
const shuffled_remaining = _.shuffle(remaining_combinations);

// Distribute: 3 to math, 2 to commonsense (plus the guaranteed solvable)
var math_conditions = shuffled_remaining.slice(0, 3);
var commonsense_conditions = [commonsense_unsolvable, ...shuffled_remaining.slice(3)];

console.log('math conditions:');
console.log(math_conditions);
console.log('commonsense conditions:'); 
console.log(commonsense_conditions);

// Get commonsense categories
var all_categories = ['Unknowns', 'Underspecification', 'False presumptions'] //_.shuffle(_.uniq(trials.filter(trial => trial.domain === 'commonsense').map(trial => trial.category)));
console.log('commonsense categories');
console.log(all_categories);

var filler_risk_condition = _.sample(['default']); // 'high_risk', 'low_risk', 


const n_vignettes = 6;
const n_fillers = 1;

// get unique item_id's of domain==math items, and sample n_vignettes/2 of them
var math_item_ids = _.shuffle(_.uniq(trials.filter(trial => trial.domain === 'math').map(trial => trial.item_id))).slice(0, n_vignettes/2);
console.log(math_item_ids);
{/* var math_items = math_item_ids.map(item_id => trials.find(trial => trial.item_id === item_id && trial.domain === 'math')); */}

// iterate over math_conditions and get one different item ID for each condition, filtered to the respective is_solvable condition
const math_items = [];
for (let i = 0; i < 3; i++) {
  const is_solvable = math_conditions[i].split('-')[1];
  var math_item_id = math_item_ids[i];
  var math_item = trials.find(trial => trial.item_id === math_item_id && trial.domain === 'math' && trial.should_abstain === (is_solvable === 'solvable' ? 'Solvable' : 'Not solvable'));
  math_item.risk_condition = math_conditions[i].split('-')[0];
  math_item.is_solvable = is_solvable;
  math_items.push(math_item);
};
console.log("math items:");
console.log(math_items);
// Build valid category-condition mapping by assigning categories sequentially
var category_condition_mapping = [];
// {/* var assigned_category_indices = new Set(); */}
const commonsense_items = [];
for (let i = 0; i < 3; i++) {
  const condition = commonsense_conditions[i];
  const is_solvable = condition.split('-')[1];
  // Find a valid category for this condition
  // for (let j = 0; j < all_categories.length; j++) {
  //   if (assigned_category_indices.has(j)) continue;
    
  const category = all_categories[i];
    
  // If solvable, skip Unknowns category; if unsolvable, any category works
  // if (is_solvable && category === 'Unknowns') continue;
  
  // category_condition_mapping.push([condition, category]);
  // assigned_category_indices.add(j);
  // break;
  var commonsense_item = trials.find(trial => trial.domain === 'commonsense' && trial.should_abstain === (is_solvable === 'solvable' ? 'Solvable' : 'Not solvable') && trial.category === category);
  if (commonsense_item) {
    commonsense_item.risk_condition = condition.split('-')[0];
    commonsense_item.is_solvable = is_solvable;
    commonsense_items.push(commonsense_item);
  }
  // }
}

console.log('commonsense category items (guaranteed valid):');
console.log(commonsense_items);

// zip the items with the main_conditions
const main_trials = math_items.concat(commonsense_items);
console.log('---- main items after all processing: ----');
console.log(main_trials);

// sample filler trial
var filler_trials = _.shuffle(fillers).slice(0, n_fillers);
// add filler_risk_condition to filler trials
filler_trials = filler_trials.map(trial => {
  trial.risk_condition = filler_risk_condition;
  return trial;
});

// concat filler and main trials, and shuffle the resulting list
var all_trials = _.shuffle(main_trials.concat(filler_trials));
console.log('---- all trials after adding fillers and shuffling: ----');
console.log(all_trials);

// Disable selecting text
// Supported by the following browsers:
// https://caniuse.com/mdn-api_document_selectstart_event
document.onselectstart = () => false;

// Disable context menu
// Supported by the following browsers:
// https://caniuse.com/mdn-api_element_contextmenu_event
document.oncontextmenu = () => false;

export default {
  name: 'App',
  components: {TwoStepScreen, TotalPointsScreen},
  data() {
    return {
      trials: _.shuffle(all_trials),
      totalPointsCache: 0
    };
  },
  computed: {
    // Expose lodash to template code
    _() {
      return _;
    }
  },
  watch: {
    '$magpie.state.trial_data': {
      handler() {
        this.$forceUpdate();
      },
      deep: true
    }
  },
  methods: {
  }
};
</script>
